import {
  ApplicationRef,
  ComponentRef,
  createComponent,
  EnvironmentInjector,
  inject,
  Injectable,
  InjectionToken,
  Injector,
  Type,
} from '@angular/core';
import { Observable, Subject } from 'rxjs';

/** Injection token replacing MAT_DIALOG_DATA. */
export const DIALOG_DATA = new InjectionToken<unknown>('DIALOG_DATA');

/** Result wrapper returned when a dialog closes. */
export interface DialogResult<T = unknown> {
  value?: T;
}

/** Configuration object passed to DialogService.open(). */
export interface DialogConfig<D = unknown> {
  data?: D;
  disableClose?: boolean;
  width?: string;
}

/**
 * Reference to an open dialog instance.
 * Callers use afterClosed() to react to the dialog result -- same pattern as MatDialogRef.
 */
export class DialogRef<R = unknown> {
  private readonly closed$ = new Subject<R | undefined>();

  constructor(
    private readonly dialogEl: HTMLDialogElement,
    private readonly componentRef: ComponentRef<unknown>,
    private readonly hostRef: ComponentRef<unknown> | null,
    private readonly appRef: ApplicationRef
  ) {}

  afterClosed(): Observable<R | undefined> {
    return this.closed$.asObservable();
  }

  close(result?: R): void {
    this.dialogEl.close();
    this.closed$.next(result);
    this.closed$.complete();
    this.cleanup();
  }

  private cleanup(): void {
    this.componentRef.destroy();
    if (this.hostRef) {
      this.appRef.detachView(this.hostRef.hostView);
      this.hostRef.destroy();
    }
    this.dialogEl.remove();
  }
}

@Injectable({ providedIn: 'root' })
export class DialogService {
  private appRef = inject(ApplicationRef);
  private injector = inject(Injector);
  private envInjector = inject(EnvironmentInjector);

  /**
   * Open a component inside a native <dialog> element.
   * Returns a DialogRef with afterClosed() observable -- drop-in replacement for MatDialog.open().
   */
  open<T, D = unknown, R = unknown>(
    component: Type<T>,
    config: DialogConfig<D> = {}
  ): DialogRef<R> {
    // 1. Create <dialog> element
    const dialog = document.createElement('dialog');
    dialog.classList.add('ts-dialog');
    if (config.width) {
      dialog.style.width = config.width;
    }
    document.body.appendChild(dialog);

    // 2. Create a wrapper div to host the Angular component
    const host = document.createElement('div');
    dialog.appendChild(host);

    // 3. Build an injector that provides DialogRef and DIALOG_DATA
    const dialogRef = new DialogRef<R>(dialog, null as any, null, this.appRef);

    const dialogInjector = Injector.create({
      parent: this.injector,
      providers: [
        { provide: DialogRef, useValue: dialogRef },
        { provide: DIALOG_DATA, useValue: config.data ?? null },
      ],
    });

    // 4. Dynamically create the content component
    const componentRef = createComponent(component, {
      hostElement: host,
      environmentInjector: this.envInjector,
      elementInjector: dialogInjector,
    });

    // Patch the DialogRef with the real componentRef
    (dialogRef as any).componentRef = componentRef;

    this.appRef.attachView(componentRef.hostView);

    // 5. Show the dialog (native modal with backdrop, focus-trap, Escape)
    dialog.showModal();

    // 6. Handle Escape / backdrop click via the native 'close' event
    dialog.addEventListener('close', () => {
      if (!dialogRef['closed$'].closed) {
        dialogRef.close(undefined);
      }
    });

    // Prevent closing on Escape if disableClose is true
    if (config.disableClose) {
      dialog.addEventListener('cancel', (e: Event) => {
        e.preventDefault();
      });
    }

    // Close on backdrop click (click on ::backdrop fires on the dialog element itself)
    if (!config.disableClose) {
      dialog.addEventListener('click', (e: MouseEvent) => {
        const rect = dialog.getBoundingClientRect();
        const clickedOutside =
          e.clientX < rect.left ||
          e.clientX > rect.right ||
          e.clientY < rect.top ||
          e.clientY > rect.bottom;
        if (clickedOutside) {
          dialogRef.close(undefined);
        }
      });
    }

    return dialogRef;
  }
}
